import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { Download } from 'lucide-react';
import { useAuthStore } from '@auth/authStore';
import { useRegisterFilters, useUrlAssetScope } from '@hooks';
import { filterDefinitions, getSearchText } from '@entities';
import { FilterBar, TablePagination } from '@components';
import { historyAnomalyKey } from '@/entities/history/history.model';
import { AnomalyDetail } from './components';
import { downloadCSV } from '@shared/utils/exportCSV';
import RegisterTable from '@/shared/components/RegisterTable/RegisterTable';
import DetailDialog from '@/shared/components/DetailDialog/DetailDialog';
import styles from './AnomalyRegister.module.css';

const text = value => String(value ?? '').trim();

function valueLabel(value) {
  return Array.isArray(value) ? value.join(' · ') : String(value ?? '—');
}

function renderCell(row, column) {
  const value = valueLabel(row[column.key]);
  if (column.key === 'rds_subsystem') return <span className={styles.groupLabel}>{value}</span>;
  if (column.key === 'inspection_technique') return <span className={styles.techniques}>{value}</span>;
  return value;
}

function RegisterContent({ data, columns, companyId, siteId, assetId }) {
  const [params, setParams] = useSearchParams();
  const filters = useRegisterFilters({
    namespace: 'anomalies', items: data, definitions: filterDefinitions, getSearchText,
  });
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [pagination, setPagination] = useState({ key: '', index: 0 });

  const selectedId = params.get('anomalies.selected');
  // Resolve against all scoped records, even when table filters hide the row.
  const matches = selectedId
    ? data.filter(row => historyAnomalyKey(row, companyId) === selectedId) : [];
  const selected = matches.length === 1 ? matches[0] : null;
  const hiddenByFilters = selected && !filters.filtered.includes(selected);

  function select(row = null) {
    setParams(previous => {
      const next = new URLSearchParams(previous);
      if (row) next.set('anomalies.selected', historyAnomalyKey(row, companyId));
      else next.delete('anomalies.selected');
      return next;
    });
  }

  const sortedData = useMemo(() => {
    if (!sortConfig.key) return filters.filtered;
    const direction = sortConfig.direction === 'asc' ? 1 : -1;
    return [...filters.filtered].sort((a, b) => direction *
      String(a[sortConfig.key] ?? '').localeCompare(String(b[sortConfig.key] ?? ''),
        undefined, { numeric: true, sensitivity: 'base' }));
  }, [filters.filtered, sortConfig]);

  const paginationKey = JSON.stringify([filters.query, filters.values, sortConfig, rowsPerPage]);
  const lastPage = Math.max(0, Math.ceil(sortedData.length / rowsPerPage) - 1);
  const page = pagination.key === paginationKey ? Math.min(pagination.index, lastPage) : 0;
  const paginated = sortedData.slice(page * rowsPerPage, (page + 1) * rowsPerPage);

  return <>
    <div className={styles.container}>
      <FilterBar filters={filters} noun="anomalies" actions={
        <button type="button" className={styles.exportButton} disabled={!sortedData.length}
          onClick={() => downloadCSV(sortedData,
            `${siteId || 'allSites'}-${assetId || 'allAssets'}-anomalies.csv`)}>
          <Download size={16} aria-hidden="true" />Export CSV
        </button>
      } />
      {selectedId && !selected && <div className={styles.notice} role="status">
        <p>{matches.length > 1
          ? 'More than one anomaly has this identity. The record cannot be opened safely.'
          : 'The selected anomaly is not available in the current company and location scope.'}</p>
        <button type="button" className={styles.link} onClick={() => select()}>Dismiss selection</button>
      </div>}
      <div className={styles.tableArea}>
        {sortedData.length ? <RegisterTable
          rows={paginated} columns={columns} sort={sortConfig} onSortChange={setSortConfig}
          onOpen={select} getRowKey={row => historyAnomalyKey(row, companyId)}
          selectedKey={selected ? selectedId : null} caption="Anomaly register" renderCell={renderCell}
        /> : <div className={styles.empty}><p>{data.length === 0
          ? 'No anomalies are available in the selected scope.'
          : 'No anomalies match the current search and filters.'}</p></div>}
      </div>
      <TablePagination count={sortedData.length} page={page} rowsPerPage={rowsPerPage}
        onPageChange={(_event, nextPage) => setPagination({ key: paginationKey, index: nextPage })}
        onRowsPerPageChange={event => setRowsPerPage(Number(event.target.value))}
        rowsPerPageOptions={[25, 50, 100]} />
    </div>
    {selected && <DetailDialog key={selectedId} eyebrow="ANOMALY"
      title={valueLabel(selected.anomaly_ref)}
      subtitle={valueLabel(selected.asset_id) + ' · ' + valueLabel(selected.site_id)}
      onClose={() => select()}>
      {hiddenByFilters && <p className={styles.notice} role="status">
        This record is open from its link. Your current table filters exclude its row.
      </p>}
      <AnomalyDetail content={selected} />
    </DetailDialog>}
  </>;
}

export default function AnomalyRegister({ data = [], columns }) {
  const companyId = useAuthStore(state => state.companyID);
  const { siteId, assetId } = useUrlAssetScope();
  const scopedData = useMemo(() => data.filter(row =>
    row && typeof row === 'object' && !Array.isArray(row) &&
    (row.company_id == null || text(row.company_id) === text(companyId)) &&
    (!siteId || text(row.site_id) === text(siteId)) &&
    (!assetId || text(row.asset_id) === text(assetId))
  ), [data, companyId, siteId, assetId]);

  if (!companyId) return <p className={styles.empty}>Select a company to view anomalies.</p>;
  return <RegisterContent key={JSON.stringify([companyId, siteId, assetId])}
    data={scopedData} columns={columns} companyId={companyId} siteId={siteId} assetId={assetId} />;
}